import React, { useCallback, useMemo, useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { encodeDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { RosterMainSection } from '../main-section/RosterMainSection';
import { SelectActivityCancellationReasonDialog } from '../../activities/activity-timeline/common/SelectActivityCancellationReasonDialog';
import { useCancelActivityFetch } from '../main-section/roster/roster-functions';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { GetRostersSidebarData_vm_activities_VOLUNTEER_NonRecurringActivityType_rosterTemplates as RosterTemplate } from '../__generated__/GetRostersSidebarData';
import { EditRosterTemplateDialog } from '../roster-templates-screen/EditRosterTemplateDialog';
import { RosterTemplateFormValues } from '../roster-templates-screen/RosterTemplateForm';

interface CalendarRosterDialogProps {
  activityId: string;
  activityDate: string;
  activityName: string;
  programName?: string;
  rosterStatus?: string;
  sessionCount?: number;
  rosterTemplateId?: string | null;
  isRosterCompleted: boolean;
  onClose: () => void;
  onRosterUpdate?: () => void;
}

export const CalendarRosterDialog: React.FC<CalendarRosterDialogProps> = ({
  activityId,
  activityDate,
  activityName,
  rosterTemplateId,
  isRosterCompleted,
  onClose,
  onRosterUpdate,
}) => {
  const { isMobile } = useCampfireTheme();
  const [showCancelActivityDialog, setShowCancelActivityDialog] = useState(false);
  const [isEditingRosterTemplate, setIsEditingRosterTemplate] = useState(false);
  const [rosterRefetchTrigger, setRosterRefetchTrigger] = useState(0);
  const cancelActivity = useCancelActivityFetch();
  const { setSnackbar } = useSnackbar();

  // Create query object that matches IndividualRosterScreen format
  const query = useMemo(
    () => ({
      activityId,
      activityDate,
    }),
    [activityId, activityDate]
  );

  const setQuery = useCallback(() => {
    // Empty function since we don't need query manipulation in dialog
  }, []);

  // Handle close and refetch
  const handleClose = useCallback(() => {
    if (onRosterUpdate) {
      onRosterUpdate();
    }
    onClose();
  }, [onClose, onRosterUpdate]);

  // Handle refetch without closing (for One-Off Session dialog, etc.)
  const handleRefetch = useCallback(() => {
    if (onRosterUpdate) {
      onRosterUpdate();
    }
  }, [onRosterUpdate]);

  // Handle dialog key events
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleEditRosterTemplate = useCallback(() => {
    if (!rosterTemplateId) return;
    setIsEditingRosterTemplate(true);
  }, [rosterTemplateId]);

  const handleRosterTemplateSaved = useCallback(
    (values?: RosterTemplateFormValues) => {
      setIsEditingRosterTemplate(false);
      if (!values) return;

      const savedDate = values.rosterDates[0] ? encodeDate(DateTime.fromJSDate(values.rosterDates[0])) : undefined;
      if (savedDate !== activityDate || values.activityId !== activityId) {
        handleClose();
        return;
      }

      setRosterRefetchTrigger((prev) => prev + 1);
      handleRefetch();
    },
    [activityDate, activityId, handleClose, handleRefetch]
  );

  const handleRosterTemplateDeleted = useCallback(() => {
    setIsEditingRosterTemplate(false);
    handleClose();
  }, [handleClose]);

  const rosterTemplate: RosterTemplate | undefined = useMemo(
    () =>
      rosterTemplateId
        ? {
            __typename: 'VOLUNTEER_RosterTemplateType',
            rosterTemplateId,
            rosterNotes: null,
            rosterDate: activityDate,
            isPendingApproval: false,
          }
        : undefined,
    [rosterTemplateId, activityDate]
  );

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth='xl'
        fullWidth
        fullScreen={isMobile}
        style={{ zIndex: 1200 }}
        PaperProps={{
          style: {
            minHeight: isMobile ? '100vh' : '85vh',
            height: isMobile ? '100vh' : '85vh',
            maxHeight: '90vh',
          },
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1300,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          size='small'
        >
          <CloseIcon />
        </IconButton>

        {/* Dialog Content with Full Roster */}
        <DialogContent style={{ padding: '0 16px 0 16px', overflow: 'auto', height: '100%' }}>
          <RosterMainSection
            showFilters={false}
            query={query}
            onSetQuery={setQuery}
            hasRosters
            isRosterCompleted={isRosterCompleted}
            loading={false}
            handleReactivateActivityRefetch={handleRefetch}
            rosterTemplate={rosterTemplate}
            handleEditRosterTemplate={handleEditRosterTemplate}
            showTemplateEditInHeader
            setShowCancelActivityDialog={setShowCancelActivityDialog}
            rosterRefetchTrigger={rosterRefetchTrigger}
          />
        </DialogContent>
        <SelectActivityCancellationReasonDialog
          open={showCancelActivityDialog}
          handleClose={() => setShowCancelActivityDialog(false)}
          handleSelection={({
            activityCancellationReasonId,
            description,
            addedAttachments,
            attachmentCreationTokens,
          }) => {
            cancelActivity
              .run({
                activityId,
                activityDate,
                activityCancellationReasonId,
                description: JSON.stringify(description),
                addedAttachments,
                attachmentCreationTokens,
              })
              .then((res) => {
                if (!res.ok) {
                  setSnackbar({
                    open: true,
                    message: 'Unable to cancel activity',
                    variant: 'error',
                  });
                  return;
                }

                setSnackbar({
                  open: true,
                  message: 'Activity cancelled',
                  variant: 'success',
                });

                handleRefetch();
                setShowCancelActivityDialog(false);
              })
              .catch(() =>
                setSnackbar({
                  open: true,
                  message: 'Unable to cancel activity',
                  variant: 'error',
                })
              );
          }}
          activityName={activityName}
          activityDate={activityDate}
        />
      </Dialog>
      {isEditingRosterTemplate && rosterTemplateId && (
        <EditRosterTemplateDialog
          rosterTemplateId={rosterTemplateId}
          onClose={() => setIsEditingRosterTemplate(false)}
          onSaved={handleRosterTemplateSaved}
          onDeleted={handleRosterTemplateDeleted}
        />
      )}
    </>
  );
};
